import { getZIdentityConfig } from './config'
import { disposeLoginButton, renderLoginButton, type RenderLoginButtonOptions } from './loginButton'

export const LOGIN_BUTTON_TAG_NAME = 'zidentity-login-button'

const OBSERVED_ATTRIBUTES = [
  'authority',
  'client-id',
  'redirect-uri',
  'scope',
  'state',
  'code-challenge',
  'code-challenge-method',
  'response-type',
  'locale',
  'show-info',
  'minimal',
] as const

// Importing this module must not crash in a non-browser context (e.g. Next.js/Nuxt SSR):
// merely referencing `HTMLElement` as a base class at module-evaluation time throws a
// ReferenceError there. Falling back to a plain class keeps the import side-effect-free;
// registerLoginButtonElement() below is a no-op in that same environment.
const HTMLElementBase: typeof HTMLElement =
  typeof HTMLElement === 'undefined' ? (class {} as typeof HTMLElement) : HTMLElement

export class ZidentityLoginButtonElement extends HTMLElementBase {
  private extraParamsValue?: Record<string, string>

  static get observedAttributes(): readonly string[] {
    return OBSERVED_ATTRIBUTES
  }

  get extraParams(): Record<string, string> | undefined {
    return this.extraParamsValue
  }

  set extraParams(value: Record<string, string> | undefined) {
    this.extraParamsValue = value
    if (this.isConnected) {
      this.render()
    }
  }

  connectedCallback(): void {
    this.render()
  }

  disconnectedCallback(): void {
    disposeLoginButton(this)
  }

  attributeChangedCallback(): void {
    if (this.isConnected) {
      this.render()
    }
  }

  private render(): void {
    const config = getZIdentityConfig()
    const authority = this.getAttribute('authority') ?? config?.authority
    const clientId = this.getAttribute('client-id') ?? config?.clientId
    const redirectUri = this.getAttribute('redirect-uri')

    // createElement() → appendChild() → setAttribute() is a normal order to write, and on that
    // first connect the attributes the authorize URL can't be built without simply aren't there
    // yet. Render nothing instead of throwing out of connectedCallback (where an app can't even
    // catch it); attributeChangedCallback renders as soon as they arrive.
    if (!authority || !clientId || !redirectUri) {
      if (this.shadowRoot) {
        this.shadowRoot.innerHTML = ''
      }
      return
    }

    const options: RenderLoginButtonOptions = {
      authority,
      clientId,
      redirectUri,
      scope: this.getAttribute('scope') ?? undefined,
      state: this.getAttribute('state') ?? undefined,
      codeChallenge: this.getAttribute('code-challenge') ?? undefined,
      codeChallengeMethod: this.getAttribute('code-challenge-method') ?? undefined,
      responseType: this.getAttribute('response-type') ?? undefined,
      locale: this.getAttribute('locale') ?? undefined,
      showInfo: this.hasAttribute('show-info'),
      minimal: this.hasAttribute('minimal'),
      extraParams: this.extraParamsValue,
    }

    renderLoginButton(this, options)
  }
}

export function registerLoginButtonElement(): void {
  if (typeof customElements === 'undefined') {
    return
  }
  if (!customElements.get(LOGIN_BUTTON_TAG_NAME)) {
    customElements.define(LOGIN_BUTTON_TAG_NAME, ZidentityLoginButtonElement)
  }
}
